import React from "react";
import { Link } from "react-router-dom";
import { useLanguage, useTheme } from "../App";
import { Sun, Moon, Globe, ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const { language, changeLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_saloneos-booking/artifacts/eysotq4t_smartbooker%20logo.png" 
                alt="SmartBooker" 
                className="h-8 w-auto"
              />
            </Link>

            <div className="flex items-center gap-4">
              <button
                onClick={() => changeLanguage(language === "it" ? "en" : "it")}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                <Globe className="w-5 h-5" />
              </button>
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-muted transition-colors"
              >
                {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {language === "it" ? "Torna alla home" : "Back to home"}
          </Link>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <h1 className="text-3xl font-serif font-medium mb-2">Privacy Policy – SmartBooker</h1>
            <p className="text-muted-foreground mb-8">Ultimo aggiornamento: 13/03/2026</p>

            <p>
              La presente Informativa sulla Privacy descrive le modalità con cui SmartBooker raccoglie, 
              utilizza e protegge i dati personali degli utenti che utilizzano la piattaforma.
            </p>
            
            <p>
              SmartBooker è una piattaforma software che consente a saloni di parrucchieri, barber shop, 
              centri estetici e spa di gestire prenotazioni online, appuntamenti e relazioni con i clienti.
            </p>
            
            <p>
              L'informativa è redatta ai sensi del Regolamento generale sulla protezione dei dati (GDPR).
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">1. Titolare del trattamento</h2>
            <p>Il Titolare del trattamento dei dati personali è:</p>
            <div className="bg-muted/50 p-4 rounded-lg my-4">
              <p className="mb-1"><strong>AleNic Service & Consulting di Antonio Luca</strong></p>
              <p className="mb-1">Indirizzo: Via Gaetano Martino 12/4 - 95036 Randazzo (CT)</p>
              <p>Email: <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a></p>
            </div>
            <p>
              Per qualsiasi richiesta relativa al trattamento dei dati è possibile contattare il titolare 
              all'indirizzo sopra indicato.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">2. Tipologia di dati raccolti</h2>
            <p>Durante l'utilizzo della piattaforma SmartBooker possono essere raccolti diversi tipi di dati personali.</p>
            
            <h3 className="text-lg font-medium mt-6 mb-3">Dati forniti direttamente dagli utenti</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>nome e cognome</li>
              <li>indirizzo email</li>
              <li>numero di telefono</li>
              <li>dati relativi agli appuntamenti prenotati</li>
              <li>informazioni relative ai servizi selezionati</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">Dati tecnici e di utilizzo</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>indirizzo IP</li>
              <li>tipo di dispositivo</li>
              <li>browser utilizzato</li>
              <li>log di accesso alla piattaforma</li>
              <li>dati di navigazione</li>
            </ul>

            <h3 className="text-lg font-medium mt-6 mb-3">Dati relativi agli account dei saloni</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>informazioni del salone</li>
              <li>servizi offerti</li>
              <li>calendario appuntamenti</li>
              <li>dati dei clienti gestiti dal salone</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">3. Finalità del trattamento dei dati</h2>
            <p>I dati personali sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>consentire la registrazione e l'utilizzo della piattaforma SmartBooker</li>
              <li>gestire prenotazioni e appuntamenti tra clienti e saloni</li>
              <li>inviare notifiche e promemoria relativi agli appuntamenti</li>
              <li>fornire assistenza e supporto tecnico</li>
              <li>migliorare le funzionalità e la sicurezza della piattaforma</li>
              <li>adempiere agli obblighi di legge</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">4. Base giuridica del trattamento</h2>
            <p>Il trattamento dei dati personali avviene sulla base delle seguenti basi giuridiche previste dal Regolamento generale sulla protezione dei dati:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>esecuzione di un contratto o di misure precontrattuali</li>
              <li>consenso dell'utente quando richiesto</li>
              <li>adempimento di obblighi legali</li>
              <li>legittimo interesse del titolare per migliorare e proteggere il servizio</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">5. Modalità di trattamento dei dati</h2>
            <p>I dati personali sono trattati mediante strumenti informatici e telematici con misure di sicurezza adeguate per garantire:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>riservatezza</li>
              <li>integrità</li>
              <li>disponibilità dei dati</li>
            </ul>
            <p className="mt-4">
              L'accesso ai dati è limitato al personale autorizzato o ai fornitori di servizi strettamente 
              necessari al funzionamento della piattaforma.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">6. Conservazione dei dati</h2>
            <p>I dati personali sono conservati per il tempo strettamente necessario alle finalità per cui sono stati raccolti.</p>
            <p className="mt-4">In particolare:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>dati degli account dei saloni: per tutta la durata del rapporto contrattuale</li>
              <li>dati relativi alle prenotazioni: fino a 24 mesi</li>
              <li>dati tecnici e log di sicurezza: fino a 12 mesi</li>
            </ul>
            <p className="mt-4">Trascorso tale periodo i dati vengono cancellati o anonimizzati.</p>

            <h2 className="text-xl font-semibold mt-8 mb-4">7. Comunicazione e condivisione dei dati</h2>
            <p>I dati personali possono essere condivisi con fornitori di servizi necessari al funzionamento della piattaforma, tra cui:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>servizi di hosting cloud</li>
              <li>provider per invio email e notifiche</li>
              <li>servizi di pagamento online</li>
              <li>servizi di analisi e sicurezza</li>
            </ul>
            <p className="mt-4">
              Tali soggetti operano come responsabili del trattamento e trattano i dati esclusivamente 
              per conto di SmartBooker.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">8. Ruoli nel trattamento dei dati</h2>
            <p>Nel contesto delle prenotazioni:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>il salone è titolare del trattamento dei dati dei propri clienti</li>
              <li>SmartBooker agisce come responsabile del trattamento, fornendo la piattaforma tecnologica per la gestione delle prenotazioni</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">9. Diritti degli utenti</h2>
            <p>Gli utenti hanno il diritto di:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>accedere ai propri dati personali</li>
              <li>richiedere la rettifica dei dati inesatti</li>
              <li>richiedere la cancellazione dei dati</li>
              <li>limitare il trattamento</li>
              <li>opporsi al trattamento</li>
              <li>ottenere la portabilità dei dati</li>
            </ul>
            <p className="mt-4">
              Le richieste possono essere inviate all'indirizzo email del titolare indicato nella presente informativa.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">10. Trasferimento dei dati</h2>
            <p>
              I dati possono essere trattati su server situati all'interno dell'Unione Europea. 
              Nel caso in cui alcuni servizi utilizzino server situati al di fuori dell'UE, il trasferimento 
              avverrà nel rispetto delle garanzie previste dal Regolamento generale sulla protezione dei dati.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">11. Sicurezza dei dati</h2>
            <p>SmartBooker adotta misure tecniche e organizzative adeguate per proteggere i dati personali, tra cui:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>connessioni protette (HTTPS)</li>
              <li>sistemi di autenticazione sicura</li>
              <li>controlli di accesso</li>
              <li>backup periodici</li>
              <li>monitoraggio delle attività di sistema</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4">12. Modifiche alla Privacy Policy</h2>
            <p>
              SmartBooker si riserva il diritto di aggiornare la presente informativa in qualsiasi momento. 
              Eventuali modifiche saranno pubblicate su questa pagina con indicazione della data di aggiornamento.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4">13. Contatti</h2>
            <p>
              Per qualsiasi informazione relativa alla presente Privacy Policy o al trattamento dei dati 
              personali è possibile contattare:
            </p>
            <p className="mt-2">
              <strong>Email:</strong>{" "}
              <a href="mailto:info@smartbooker.it" className="text-accent">info@smartbooker.it</a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 border-t">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 SmartBooker - Powered by AleNic Service and Consulting
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
